// src/pages/geral/CidadeCepCad.js
import React, { useEffect, useRef, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import Input from '../../components/input/Input';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalTable from '../../components/modalForm/ModalTable';
import LazyLoading from '../../components/modalForm/LazyLoading';
import { ModalActionButton } from '../../components/modalForm/ModalActions';
import { useCidades } from '../../contexts/geral/CidadeContext';
import { useCidadeCep } from '../../contexts/geral/CidadeCepContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { CidadeCep, normalizarCep, formatarCep } from '../../models/geral/CidadeCep';
import CidadeSearch from './CidadeSearch';

const CidadeCepCad = ({ show, onHide, cidadeInicial, cepInicial }) => {
  const { cidades } = useCidades();
  const { listar, adicionar, excluir } = useCidadeCep();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();
  const [cidadeId, setCidadeId] = useState('');
  const [cep, setCep] = useState('');
  const [registros, setRegistros] = useState([]);
  const [busca, setBusca] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const versao = useRef(0);
  const trava = useRef(false);
  const cidade = cidades.find(item => Number(item.id) === Number(cidadeId));

  useEffect(() => {
    if (show) {
      setCidadeId(cidadeInicial || ''); setCep(cepInicial || ''); setBusca(false);
    }
  }, [show, cidadeInicial, cepInicial]);

  useEffect(() => {
    const atual = ++versao.current;
    setRegistros([]);
    setCarregando(!!(show && cidade));
    if (show && cidade) {
      listar(cidade.id).then(itens => {
        if (versao.current === atual) setRegistros(itens);
      }).catch(() => {}).finally(() => {
        if (versao.current === atual) setCarregando(false);
      });
    }
    return () => { ++versao.current; };
  }, [show, cidade?.id, listar]);

  const selecionar = (item) => { setCidadeId(item.id); setCep(''); setBusca(false); };
  const incluir = async () => {
    if (trava.current) return;
    if (!cidade) return showMessage('Selecione um município.', 'warning');
    if (normalizarCep(cep).length !== 8) return showMessage('Informe um CEP válido com oito dígitos.', 'warning');
    const atual = versao.current;
    trava.current = true; setOcupado(true);
    try {
      const salvo = await adicionar(new CidadeCep(cidade.id, cep));
      if (atual === versao.current) {
        setRegistros(itens => [...itens, salvo].sort((a, b) => a.cep.localeCompare(b.cep)));
        setCep('');
      }
    } catch (_) { }
    finally { trava.current = false; setOcupado(false); }
  };
  const remover = async (item) => {
    if (trava.current) return;
    const atual = versao.current;
    trava.current = true; setOcupado(true);
    try {
      if (!await showYesNo(`Deseja excluir o CEP ${formatarCep(item.cep)}?`)) return;
      await excluir(item.id);
      if (atual === versao.current) setRegistros(itens => itens.filter(x => x.id !== item.id));
    } catch (_) { }
    finally { trava.current = false; setOcupado(false); }
  };
  if (!show) return null;
  return <ModalContainer show={show} largura="650px" variant="cadastro" onHide={onHide}>
    <ModalHeader title="CEPs por Município" onHide={onHide} />
    <ModalBody>
      <div className="input-group">
        <Input label="Município" value={cidadeId} disabled={ocupado}
          onChange={e => { setCidadeId(e.target.value); setCep(''); }}
          btnBuscar={() => setBusca(true)} btnBuscarDisabled={ocupado} size={100} />
        <Input label="Nome" value={cidade?.nome || ''} disabled size={300} />
        <Input label="UF" value={cidade?.uf || ''} disabled size={40} />
      </div>
      <div className="input-group">
        <Input label="CEP" mask="99999-999" value={cep} onChange={e => setCep(e.target.value)}
          disabled={ocupado} size={120} />
        <ModalActionButton action="add" className="ms-0" onClick={incluir}
          disabled={!cidade || ocupado || carregando}>Adicionar CEP</ModalActionButton>
      </div>
      {carregando && <p role="status">Carregando CEPs...</p>}
      <LazyLoading items={registros} resetKey={`${show}-${cidadeId}`}>
        {({ containerRef, handleScroll, visibleItems }) => <ModalTable containerRef={containerRef} onContainerScroll={handleScroll}>
          <thead><tr><th>CEP</th><th className="text-center" style={{ width: '80px' }}>Ações</th></tr></thead>
          <tbody>{visibleItems.map(item => <tr key={item.id}>
            <td>{formatarCep(item.cep)}</td><td className="text-center">
              <button type="button" className="btn btn-outline-danger btn-sm" disabled={ocupado}
                aria-label={`Excluir CEP ${formatarCep(item.cep)}`} onClick={() => remover(item)}><FaTrash aria-hidden="true" /></button>
            </td></tr>)}
            {!carregando && registros.length === 0 && <tr><td colSpan={2}>Nenhum CEP cadastrado para o município.</td></tr>}
          </tbody>
        </ModalTable>}
      </LazyLoading>
    </ModalBody>
    <ModalFooter><ModalActionButton action="close" onClick={onHide}>Fechar</ModalActionButton></ModalFooter>
    <CidadeSearch show={busca} onHide={() => setBusca(false)} onSelectCidade={selecionar} />
  </ModalContainer>;
};
export default CidadeCepCad;
